import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { getErrorMessage } from "@/lib/errors";

import { fmtMoney, fmtDate, partyLabel } from "@/lib/format";
export default function ClientLedger() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/revenue/clients").then(({ data }) => setClients(data)).catch((err) => toast.error(getErrorMessage(err)));
  }, []);

  const [mailing, setMailing] = useState(false);
  const emailStatement = async () => {
    setMailing(true);
    try {
      const { data } = await api.post(`/revenue/clients/${clientId}/statement-email`);
      toast.success(`Statement emailed to ${data.sent_to}`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setMailing(false);
    }
  };

  useEffect(() => {
    if (!clientId) { setLedger(null); return; }
    setLoading(true);
    api.get(`/revenue/clients/${clientId}/ledger`)
      .then(({ data }) => setLedger(data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [clientId]);

  return (
    <div className="space-y-6" data-testid="client-ledger-page">
      <PageHeader title="Client Ledger" description="Outstanding receivable and ageing across RA bills for a client." />
      <div className="max-w-xs">
        <Label>Client</Label>
        <Select value={clientId} onValueChange={setClientId}>
          <SelectTrigger data-testid="ledger-client-select" className="mt-1.5"><SelectValue placeholder="Select a client" /></SelectTrigger>
          <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{partyLabel(c)}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {clientId && ledger && (() => {
        const cl = clients.find((c) => c.id === clientId);
        const em = (cl?.email || "").trim();
        return (
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" disabled={mailing || !em} onClick={emailStatement} data-testid="ledger-email-statement-button" title={em ? `Email the statement (PDF) to ${em}` : "Add the client's email under Clients first"}>
              {mailing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email statement
            </Button>
            <span className={`text-xs ${em ? "text-muted-foreground" : "text-amber-600 dark:text-amber-400"}`}>{em ? `Goes to ${em}` : "This client has no email saved"}</span>
          </div>
        );
      })()}

      {!clientId ? (
        <EmptyState title="Select a client to view their ledger" testId="client-ledger-empty-state" />
      ) : loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : ledger ? (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Outstanding</p>
              <p className="mt-1 font-mono text-lg font-bold text-foreground" data-testid="ledger-outstanding">{ledger.outstanding}</p>
            </div>
            {ledger.aging.map((bucket) => (
              <div key={bucket.label} className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground">{bucket.label} days</p>
                <p className="mt-1 font-mono text-lg font-semibold text-foreground">{bucket.amount}</p>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-border bg-card">
            {ledger.entries.length === 0 ? (
              <EmptyState title="No posted RA bills for this client yet" />
            ) : (
              <Table>
                <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Number</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead></TableRow></TableHeader>
                <TableBody>
                  {ledger.entries.map((e) => (
                    <TableRow key={e.doc_id} data-testid={`ledger-entry-row-${e.number}`}>
                      <TableCell className="text-muted-foreground">{e.doc_type.replaceAll("_", " ")}</TableCell>
                      <TableCell className="font-mono text-xs text-foreground">{e.number}</TableCell>
                      <TableCell className="text-muted-foreground">{fmtDate(e.doc_date)}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-foreground">{fmtMoney(e.debit)}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-foreground">{fmtMoney(e.credit)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
